import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import API from '../../utils/api';
import { FiUser, FiTruck, FiAlertCircle } from 'react-icons/fi';
import BookingDetailModal from '../../components/BookingDetailModal';

const MechanicSchedule = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Get dates for the current week
  const getWeekDates = () => {
    const dates = [];
    const currentDate = new Date(selectedDate);
    const day = currentDate.getDay();

    // Get Sunday of the current week
    const diff = currentDate.getDate() - day;
    const sunday = new Date(currentDate.setDate(diff));

    // Get all days of the week
    for (let i = 0; i < 7; i++) {
      const date = new Date(sunday);
      date.setDate(sunday.getDate() + i);
      dates.push(date);
    }

    return dates;
  };

  const weekDates = getWeekDates();

  const fetchBookings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Format dates for API request
      const startDate = formatDate(weekDates[0]);
      const endDate = formatDate(weekDates[6]);

      console.log(`Fetching schedule data from ${startDate} to ${endDate}`);

      // Menggunakan endpoint mechanic-tasks yang baru
      const response = await API.get(`/api/mechanic-tasks/date-range?startDate=${startDate}&endDate=${endDate}`);
      console.log('Schedule data:', response.data);

      setBookings(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching schedule:', err);

      // Extract error message from API response or use a default message
      const errorMessage = err.response?.data?.message || 'Gagal memuat jadwal';
      setError(errorMessage);

      // Set empty bookings array to avoid undefined issues
      setBookings([]);
      setLoading(false);
    }
  }, [weekDates]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDisplayDate = (date) => {
    const options = { weekday: 'short', day: 'numeric' };
    return date.toLocaleDateString('id-ID', options);
  };

  const handleStatusChange = async (bookingId, newStatus) => {
    try {
      // Menggunakan endpoint mechanic-tasks yang baru
      await API.put(`/api/mechanic-tasks/${bookingId}/status`, { status: newStatus });

      // Update local state
      setBookings(bookings.map(booking =>
        booking.id === bookingId ? { ...booking, task_status: newStatus } : booking
      ));

      // Close modal if open
      if (showModal && selectedBooking && selectedBooking.id === bookingId) {
        setSelectedBooking({ ...selectedBooking, task_status: newStatus });
      }
    } catch (err) {
      console.error('Error updating booking status:', err);

      // Extract error message from API response or use a default message
      const errorMessage = err.response?.data?.message || 'Gagal mengubah status pekerjaan';
      alert(errorMessage);
    }
  };

  const handleOpenDetails = (booking) => {
    // Map properties dari response API ke format yang diharapkan oleh BookingDetailModal
    const formattedBooking = {
      id: booking.id,
      service_name: booking.service_name || 'Layanan tidak tersedia',
      booking_date: booking.date || 'Tanggal tidak tersedia',
      booking_time: booking.time || 'Waktu tidak tersedia',
      task_status: booking.task_status || booking.booking_status || 'pending',
      customer_name: booking.customer_name || 'Pelanggan',
      make: booking.make || 'N/A',
      model: booking.model || '',
      license_plate: booking.license_plate || 'Tidak tersedia',
      estimated_time: booking.estimated_time || '60',
      customer_notes: booking.customer_notes || '',
      task_id: booking.task_id || booking.id
    };

    setSelectedBooking(formattedBooking);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedBooking(null);
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + (direction * 7));
    setSelectedDate(newDate);
  };

  const getBookingsForDate = (date) => {
    const dateStr = formatDate(date);
    return bookings.filter(booking => booking.date === dateStr);
  };

  const getStatusClass = (status) => {
    // Status bisa dari task_status atau booking_status
    const bookingStatus = status || 'pending';

    switch (bookingStatus) {
      case 'pending':
        return 'border-yellow-400 bg-yellow-50';
      case 'in-progress':
        return 'border-blue-400 bg-blue-50';
      case 'completed':
        return 'border-green-400 bg-green-50';
      case 'cancelled':
        return 'border-red-400 bg-red-50';
      default:
        return 'border-gray-300 bg-white';
    }
  };

  if (loading) {
    return (
      <DashboardLayout userRole="mechanic" userName="Montir">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userRole="mechanic" userName="Montir">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-secondary">Jadwal Booking</h1>
        <p className="text-gray-600">Lihat jadwal pekerjaan Anda</p>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6 flex items-center justify-between">
          <div className="flex items-center">
            <FiAlertCircle className="mr-2" />
            <span>{error}</span>
          </div>
          <button
            onClick={fetchBookings}
            className="bg-red-600 hover:bg-red-700 text-white py-1 px-3 rounded"
          >
            Coba Lagi
          </button>
        </div>
      )}

      {/* Week Navigation */}
      <div className="flex justify-between items-center mb-6">
        <button
          onClick={() => navigateWeek(-1)}
          className="bg-white border border-gray-300 rounded-md px-4 py-2 text-gray-600 hover:bg-gray-50"
        >
          Minggu Sebelumnya
        </button>
        <div className="font-medium">
          {formatDisplayDate(weekDates[0])} - {formatDisplayDate(weekDates[6])}
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => navigateWeek(1)}
            className="bg-white border border-gray-300 rounded-md px-4 py-2 text-gray-600 hover:bg-gray-50"
          >
            Minggu Berikutnya
          </button>
          <button
            onClick={fetchBookings}
            className="bg-primary text-white px-4 py-2 rounded-md flex items-center"
          >
            <FiAlertCircle className="mr-2" /> Refresh
          </button>
        </div>
      </div>

      {/* Weekly Calendar */}
      <div className="grid grid-cols-7 gap-2">
        {weekDates.map((date, index) => {
          const dateBookings = getBookingsForDate(date);
          const isToday = new Date().toDateString() === date.toDateString();

          return (
            <div key={index} className={`border rounded-lg ${isToday ? 'border-primary' : 'border-gray-200'}`}>
              <div className={`p-2 text-center font-medium ${isToday ? 'bg-primary text-white' : 'bg-gray-50'}`}>
                {formatDisplayDate(date)}
              </div>
              <div className="p-2 h-96 overflow-y-auto">
                {dateBookings.length === 0 ? (
                  <div className="text-center text-gray-400 mt-4">Tidak ada booking</div>
                ) : (
                  dateBookings.map(booking => {
                    // Tentukan status yang digunakan (task_status atau booking_status)
                    const bookingStatus = booking.task_status || booking.booking_status || 'pending';

                    return (
                      <div
                        key={booking.id}
                        className={`p-2 mb-2 border-l-4 rounded cursor-pointer ${getStatusClass(bookingStatus)}`}
                        onClick={() => handleOpenDetails(booking)}
                      >
                        <div className="font-medium">{booking.time} - {booking.service_name}</div>
                        <div className="flex items-center text-sm text-gray-600">
                          <FiUser className="mr-1" size={12} />
                          <span>{booking.customer_name}</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <FiTruck className="mr-1" size={12} />
                          <span>{booking.license_plate}</span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Detail Modal */}
      {showModal && selectedBooking && (
        <BookingDetailModal
          booking={selectedBooking}
          onClose={closeModal}
          onStatusChange={handleStatusChange}
        />
      )}
    </DashboardLayout>
  );
};

export default MechanicSchedule; 
